import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TokenStorage } from '../../core/token-storage';
import { UsersService } from '../users.service';
import { ExternalsParametersService } from '../external-parameters.service';
import { CookieConsentService } from '../cookie-consent.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private CESAR_ACCESS_TOKEN = '__access_token__';
  private EXTERNAL_PARAMETER = 'parameters';

  constructor(
    private router: Router,
    private usersService: UsersService,
    private tokenStorage: TokenStorage,
    private externalsParametersService: ExternalsParametersService,
    private cookieConsentService: CookieConsentService,
    private toastr: ToastrService,
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    if (!this.cookieConsentService.hasConsent()) {
      console.warn('Consentement aux cookies manquant → redirection login');
      this.router.navigate(['/login']);
      return false;
    }
    const ssoToken = next.queryParams[this.CESAR_ACCESS_TOKEN];
    const externalParameters = next.queryParams[this.EXTERNAL_PARAMETER];
    const currentUrl = state.url;

    if (ssoToken) {
      console.log("Token trouvé dans l'URL:", ssoToken);

      this.usersService.removeCurrentUser();

      return this.usersService.checkCurrentUser(ssoToken).pipe(
        map((currentUser) => {
          if (currentUser) {
            if (!this.hasNexusCentrexAccess(currentUser)) {
              console.warn('Accès NexusCentrex refusé');

              this.usersService.removeCurrentUser();

              this.toastr.error(
                "Vous n'avez pas les droits nécessaires pour accéder à Nexus Centrex.",
                'Accès refusé',
              );

              this.router.navigate(['/login']);

              return false;
            }

            console.log('Utilisateur authentifié avec succès:', currentUser);

            this.tokenStorage.saveToken(ssoToken);

            if (externalParameters) {
              console.log('Paramètres externes trouvés :', externalParameters);
              try {
                const parsedParameters = JSON.parse(externalParameters);
                this.externalsParametersService.loadExternalParameters(
                  parsedParameters,
                );
              } catch (error) {
                console.error(
                  'Erreur lors du parsing des paramètres externes :',
                  error,
                );
              }
            }

            const params = { ...next.queryParams };
            delete params[this.CESAR_ACCESS_TOKEN];
            delete params[this.EXTERNAL_PARAMETER];
            this.router.navigate([state.url.split('?')[0]], {
              queryParams: params,
            });

            if (currentUrl === '/login') {
              this.router.navigate(['/dashboard']);
            }
            return true;
          } else {
            this.router.navigate(['/login']);
            return false;
          }
        }),
        catchError(() => {
          this.router.navigate(['/login']);
          return of(false);
        }),
      );
    }

    return this.usersService.getCurrentUser().pipe(
      map((currentUser) => {
        if (currentUser) {
          if (!this.hasNexusCentrexAccess(currentUser)) {
            console.warn('Accès NexusCentrex refusé');

            this.usersService.removeCurrentUser();

            this.toastr.error(
              "Vous n'avez pas les droits nécessaires pour accéder à Nexus Centrex.",
              'Accès refusé',
            );

            this.router.navigate(['/login']);

            return false;
          }

          if (currentUrl === '/login') {
            this.router.navigate(['/dashboard']);
          }

          return true;
        } else {
          this.usersService.removeCurrentUser();
          this.router.navigate(['/login']);
          return false;
        }
      }),
      catchError(() => {
        this.router.navigate(['/login']);
        return of(false);
      }),
    );
  }

  private hasNexusCentrexAccess(user: any): boolean {
    const property = user?.properties?.find(
      (p: any) => p.name?.trim() === 'NexusCentrex',
    );

    // propriété absente => refus
    if (!property?.value) {
      return false;
    }

    const value = property.value;

    // seuls ReadOnly et FullAccess sont autorisés
    return value.includes('1') || value.includes('2');
  }
}
